import { UploadIcon } from "@minket/brand/icons/upload";
import { limits } from "@minket/core";
import { Button } from "@minket/ui/components/button";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { lazy } from "react";
import { OnDemand, Time } from "@/components/audience/kit";
import { StepPage } from "@/components/onboarding/step-page";
import { UnavailableAction } from "@/components/page/action";
import { describeError } from "@/lib/api";
import { endpoints } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiQuery } from "@/lib/query";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/onboarding/contacts")({
  component: ContactsStep,
});

const inlineMegabytes = limits.inlineImportBytesMax / (1024 * 1024);
const load = () => import("@/components/audience/contact-import.ondemand");
const ImportDialog = lazy(() => load().then((m) => ({ default: m.ImportDialog })));
const importContacts = { label: "Import contacts", icon: UploadIcon };

function ContactsStep() {
  return (
    <StepPage
      path="/onboarding/contacts"
      intro="Import contacts from a CSV with their properties and consent, so broadcasts and automations have an audience."
      asks={[
        {
          title: "A CSV file",
          text: `One contact per row with an email column. Files up to ${inlineMegabytes} MB go straight in; larger ones are uploaded first.`,
        },
        { title: "Consent", text: "Whether each contact opted in, and to which topics." },
      ]}
      action={importContacts}
      control={<StepControl />}
    />
  );
}

function StepControl() {
  const { project } = useWorkspace();
  const canManage = useCan("audience.manage");
  const list = useQuery({
    ...apiQuery(endpoints.contactImports, { projectId: project?.id ?? "" }),
    enabled: !!project,
  });
  const unavailable = (reason: string) => (
    <UnavailableAction {...importContacts} reason={reason} align="start" />
  );
  if (!project) return unavailable("Choose a project first");
  if (!canManage) return unavailable("Viewers cannot import contacts");
  if (list.isPending) return unavailable("Loading imports");
  if (list.isError) return unavailable(describeError(list.error).message);
  const last = list.data.data[0];
  return (
    <div className="grid gap-2">
      <OnDemand
        load={load}
        button={(props) => (
          <Button className="w-fit" {...props}>
            <UploadIcon size={20} className="size-5" />
            Import contacts
          </Button>
        )}
        dialog={(props) => <ImportDialog projectId={project.id} {...props} />}
      />
      {last ? (
        <p
          className="flex flex-wrap items-baseline gap-x-2 text-sm text-muted-foreground"
          role="status"
        >
          <span>
            The last import{" "}
            {last.status === "completed"
              ? "finished"
              : last.status === "failed"
                ? "failed"
                : "is running"}
          </span>
          <Time value={last.created_at} className="text-foreground" />
          <span className="whitespace-nowrap text-foreground">
            {last.counts.created} added, {last.counts.updated} updated, {last.counts.failed} failed
          </span>
        </p>
      ) : null}
    </div>
  );
}
